import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { can, type Action, type PermissionScope, type Principal } from '@saas-manager/shared';
import { useToast } from '@/components/ui/core';
import { api, ApiError } from './api';
import { useSession } from './session';

/** GET with the URL as cache key. */
export function useApi<T>(url: string | null, opts: Partial<UseQueryOptions<T, ApiError>> = {}) {
  return useQuery<T, ApiError>({ queryKey: [url], queryFn: () => api.get<T>(url!), enabled: Boolean(url), ...opts });
}

/**
 * Mutation that toasts its outcome and refreshes the given cache prefixes.
 * The success message names the completed action ("Sauvegarde lancée").
 */
export function useAction<TInput, TOut = unknown>(fn: (input: TInput) => Promise<TOut>, opts: { success?: string | ((out: TOut) => string); invalidate?: string[]; onSuccess?: (out: TOut) => void } = {}) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation<TOut, ApiError, TInput>({
    mutationFn: fn,
    onSuccess: (out) => {
      if (opts.success) toast('success', typeof opts.success === 'function' ? opts.success(out) : opts.success);
      for (const prefix of opts.invalidate ?? ['']) {
        qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0] ?? '').startsWith(prefix) });
      }
      opts.onSuccess?.(out);
    },
    onError: (e) => toast('error', e.message),
  });
}

/** UI-side permission hint. The server remains the only enforcement point. */
export function useCan() {
  const { principal } = useSession();
  return (action: Action, scope?: PermissionScope) => (principal ? can(principal as Principal, action, scope) : false);
}
